import { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { usePlan } from '@/hooks/usePlan';
import { getAppUrl } from '@/lib/appUrl';
import { useTranslation } from '@/i18n/LanguageContext';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { WorkflowAuditHistory } from '@/components/workflow/WorkflowAuditHistory';
import {
  Share2, Copy, Check, Users, Lock, Crown, Link2, Radio,
} from 'lucide-react';

interface ShareWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string | null;
  workspaceName: string;
  /** Current co-op enabled state (from DB). */
  coopEnabled?: boolean;
  /** Owner-only: flip the DB `coop_enabled` flag. Resolves to new state on success. */
  onToggleCoopEnabled?: (enabled: boolean) => Promise<boolean>;
}

export function ShareWorkspaceDialog({
  open,
  onOpenChange,
  workspaceId,
  workspaceName,
  coopEnabled = false,
  onToggleCoopEnabled,
}: ShareWorkspaceDialogProps) {
  const { isAuthenticated, isEmailVerified, user } = useAuth();
  const { limits } = usePlan();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [coopOn, setCoopOn] = useState(coopEnabled);
  const [toggling, setToggling] = useState(false);
  useEffect(() => { setCoopOn(coopEnabled); }, [coopEnabled]);

  const [inviting, setInviting] = useState(false);

  const handleInvite = useCallback(async () => {
    if (!workspaceId || !inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data, error } = await supabase.functions.invoke('coop-invite-collaborator', {
        body: { email: inviteEmail.trim(), workflow_id: workspaceId, role: 'editor' },
      });
      if (error) throw error;
      if (data?.error === 'no_account') {
        toast({ title: data.message || 'Ingen användare hittades', variant: 'destructive' });
      } else if (data?.success) {
        toast({
          title: 'Inbjudan skickad',
          description: data.email_sent ? 'E-postnotis utgick.' : 'Tillagd utan e-post — ge länken direkt.',
        });
        setInviteEmail('');
      } else if (data?.error) {
        toast({ title: String(data.error), variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: (err as Error).message, variant: 'destructive' });
    }
    setInviting(false);
  }, [workspaceId, inviteEmail]);

  const handleToggleCoop = useCallback(async (next: boolean) => {
    if (!onToggleCoopEnabled) return;
    setToggling(true);
    try {
      const ok = await onToggleCoopEnabled(next);
      if (ok) {
        setCoopOn(next);
        toast({ title: next ? t('coopActive') : t('coopInactive') });
      }
    } catch {
      toast({ title: t('coopLoadFailed'), variant: 'destructive' });
    } finally {
      setToggling(false);
    }
  }, [onToggleCoopEnabled, t]);

  // Q-102: personalize share-link så mottagaren ser en toast med vem som
  // delade + workflow-namn. `user_metadata.display_name` faller tillbaka
  // till email-prefix om ingen display_name finns. Båda parametrar
  // url-encode:as så special-tecken i namn hanteras korrekt.
  const sharedByName = (() => {
    const meta = (user?.user_metadata ?? {}) as { display_name?: string; full_name?: string };
    const fromMeta = meta.display_name || meta.full_name;
    if (fromMeta) return fromMeta;
    const email = user?.email;
    if (email && email.includes('@')) return email.split('@')[0];
    return '';
  })();
  const shareUrl = workspaceId
    ? (() => {
        const base = `${getAppUrl()}/?workspace=${workspaceId}&mode=coop`;
        const extras: string[] = [];
        if (sharedByName) extras.push(`sharedBy=${encodeURIComponent(sharedByName)}`);
        if (workspaceName) extras.push(`name=${encodeURIComponent(workspaceName)}`);
        return extras.length > 0 ? `${base}&${extras.join('&')}` : base;
      })()
    : '';

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [shareUrl]);

  const canCoop = limits.maxCoopWorkspaces > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Dela arbetsyta
          </DialogTitle>
          <DialogDescription>
            {workspaceName || 'Unnamed workspace'}
          </DialogDescription>
        </DialogHeader>

        {!isAuthenticated ? (
          <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
            <Lock className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Du måste vara inloggad för att dela arbetsytor.
            </p>
          </div>
        ) : !isEmailVerified ? (
          <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
            <Lock className="w-8 h-8 mx-auto text-amber-500" />
            <p className="text-sm text-muted-foreground">
              Bekräfta din e-postadress innan du kan dela arbetsytor.
            </p>
            <p className="text-xs text-muted-foreground">
              Kolla inkorgen för bekräftelsemejlet — du kan fortfarande använda resten av appen.
            </p>
          </div>
        ) : !canCoop ? (
          <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
            <Crown className="w-8 h-8 mx-auto text-amber-500" />
            <p className="text-sm text-muted-foreground">
              Co-op arbetsytor kräver Advanced eller högre.
            </p>
            <p className="text-xs text-muted-foreground">
              Din plan: <span className="font-semibold">{limits.name}</span> — 0 co-op arbetsytor
            </p>
          </div>
        ) : !workspaceId ? (
          <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
            <Users className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Spara arbetsytan till molnet först för att kunna dela den.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Owner co-op toggle */}
            {onToggleCoopEnabled && (
              <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <Radio className={`w-4 h-4 ${coopOn ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                    {t('coopEnableTitle')}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('coopEnableHint')}
                  </p>
                </div>
                <Switch
                  checked={coopOn}
                  disabled={toggling}
                  onCheckedChange={handleToggleCoop}
                  data-testid="toggle-coop-enabled"
                />
              </div>
            )}

            {/* Share link — only useful once coop is enabled */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Delningslänk (kräver inloggning)</label>
              <div className="flex gap-2">
                <Input
                  value={shareUrl}
                  readOnly
                  className="text-xs font-mono"
                  disabled={onToggleCoopEnabled ? !coopOn : false}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  disabled={onToggleCoopEnabled ? !coopOn : false}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Bara inloggade användare med rätt behörighet kan öppna länken
              </p>
            </div>

            {/* Invite by email */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Bjud in med e-post</label>
              <div className="flex gap-2">
                <Input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="kollega@foretag.se"
                  type="email"
                  className="flex-1"
                />
                <Button
                  size="sm"
                  disabled={!inviteEmail.trim() || inviting || !coopOn}
                  onClick={handleInvite}
                >
                  <Users className="w-4 h-4 mr-1" />
                  Bjud in
                </Button>
              </div>
            </div>

            {/* Status */}
            <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
              <div className="flex items-center gap-2">
                <Link2 className="w-3.5 h-3.5" />
                <span>Co-op: {limits.maxCoopWorkspaces} delade arbetsytor ({limits.name})</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-3.5 h-3.5" />
                <span>All data krypteras end-to-end</span>
              </div>
            </div>

            {/* Audit history — extraherat till separat komponent A-101b */}
            <WorkflowAuditHistory workspaceId={workspaceId} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
